"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import type { ApiWave } from "@/generated/models/ApiWave";
import { commonApiFetch } from "@/services/api/common-api";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { ExploreWaveCard } from "./ExploreWaveCard";
import { ExploreWaveCardSkeleton } from "./ExploreWaveCardSkeleton";
const DEFAULT_WAVES_LIMIT = 6;
interface ExploreWavesSectionProps {
  readonly title?: string | undefined;
  readonly subtitle?: string | null | undefined;
  readonly limit?: number | undefined;
  readonly endpoint?: string | undefined;
  readonly viewAllHref?: string | null | undefined;
  readonly excludeFollowed?: boolean | undefined;
}
export function ExploreWavesSection(t0) {
  const $ = _c(35);
  const {
    title: t1,
    subtitle: t2,
    limit: t3,
    endpoint: t4,
    viewAllHref: t5,
    excludeFollowed: t6
  } = t0;
  const title = t1 === undefined ? "Tired of bot replies? Join the most interesting chats in crypto" : t1;
  const subtitle = t2 === undefined ? "Most active waves" : t2;
  const limit = t3 === undefined ? DEFAULT_WAVES_LIMIT : t3;
  const endpoint = t4 === undefined ? "waves-overview/hot" : t4;
  const viewAllHref = t5 === undefined ? "/waves" : t5;
  const excludeFollowed = t6 === undefined ? false : t6;
  const {
    connectedProfile
  } = useAuth();
  const userScope = connectedProfile?.id ?? connectedProfile?.normalised_handle ?? connectedProfile?.handle ?? null;
  const t7 = excludeFollowed ? userScope : null;
  let t8;
  if ($[0] !== endpoint || $[1] !== excludeFollowed || $[2] !== limit || $[3] !== t7) {
    t8 = ["explore-waves", endpoint, limit, excludeFollowed, t7];
    $[0] = endpoint;
    $[1] = excludeFollowed;
    $[2] = limit;
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== endpoint || $[6] !== excludeFollowed || $[7] !== limit) {
    t9 = async () => {
      const data = await commonApiFetch({
        endpoint,
        params: excludeFollowed ? {
          exclude_followed: "true"
        } : undefined
      });
      return data.slice(0, limit);
    };
    $[5] = endpoint;
    $[6] = excludeFollowed;
    $[7] = limit;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = excludeFollowed ? 0 : 300000;
  let t11;
  if ($[9] !== excludeFollowed) {
    t11 = excludeFollowed ? {
      gcTime: 0
    } : {};
    $[9] = excludeFollowed;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t10 || $[12] !== t11 || $[13] !== t8 || $[14] !== t9) {
    t12 = {
      queryKey: t8,
      queryFn: t9,
      staleTime: t10,
      ...t11
    };
    $[11] = t10;
    $[12] = t11;
    $[13] = t8;
    $[14] = t9;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const {
    data: waves,
    isLoading,
    isError
  } = useQuery(t12);
  if (isError) {
    return null;
  }
  if (!isLoading && (!waves || waves.length === 0)) {
    return null;
  }
  let t13;
  if ($[16] !== title) {
    t13 = <span className="tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-iron-200 md:tw-text-2xl">{title}</span>;
    $[16] = title;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== subtitle) {
    t14 = subtitle && <p className="tw-mb-0 tw-mt-2 tw-text-base tw-text-iron-500">{subtitle}</p>;
    $[18] = subtitle;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t13 || $[21] !== t14) {
    t15 = <div className="tw-mb-8 tw-flex tw-flex-col tw-items-start tw-gap-4 md:tw-items-end"><div className="tw-max-w-sm md:tw-mx-auto md:tw-max-w-xl md:tw-text-center lg:tw-max-w-full">{t13}{t14}</div></div>;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== isLoading || $[24] !== limit || $[25] !== waves) {
    t16 = isLoading ? Array.from({
      length: limit
    }).map(_temp) : waves?.map(_temp2);
    $[23] = isLoading;
    $[24] = limit;
    $[25] = waves;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t16) {
    t17 = <div className="tw-grid tw-grid-cols-1 tw-gap-x-3 tw-gap-y-4 sm:tw-grid-cols-2 sm:tw-gap-6 lg:tw-grid-cols-3">{t16}</div>;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== viewAllHref) {
    t18 = viewAllHref && <div className="tw-mt-8 tw-flex tw-justify-center md:tw-mt-10"><Link href={viewAllHref} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-iron-400 tw-no-underline tw-transition-colors hover:tw-text-white"><span>View all</span><ArrowRightIcon className="tw-size-4 tw-flex-shrink-0" aria-hidden={true} /></Link></div>;
    $[29] = viewAllHref;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t15 || $[32] !== t17 || $[33] !== t18) {
    t19 = <section className="tw-px-4 tw-py-10 md:tw-px-6 md:tw-py-16 lg:tw-px-8"><div>{t15}{t17}{t18}</div></section>;
    $[31] = t15;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  return t19;
}
function _temp2(wave) {
  return <div key={wave.id} className="tw-w-full"><ExploreWaveCard wave={wave} /></div>;
}
function _temp(_, index) {
  return <div key={`skeleton-${index}`} className="tw-w-full"><ExploreWaveCardSkeleton /></div>;
}